import api from "@/utils/axios";

const SETTINGS_API_URL = `/settings`;

const storedResolved = localStorage.getItem("settings_resolved");

export default {
  namespaced: true,

  state: {
    resolved: storedResolved ? JSON.parse(storedResolved) : null,
    scoped: null,
    loading: false,
    error: null,
  },

  mutations: {
    SET_RESOLVED(state, payload) {
      state.resolved = payload;
    },
    SET_SCOPED(state, payload) {
      state.scoped = payload;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
  },

  actions: {
    async loadResolvedSettings({ commit }, { force = false } = {}) {
      const stored = localStorage.getItem("settings_resolved");

      if (!force && stored) {
        const parsed = JSON.parse(stored);
        commit("SET_RESOLVED", parsed);
        return parsed;
      }

      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        const response = await api.get(`${SETTINGS_API_URL}/resolved/`, {
          errorNotify: true,
        });

        commit("SET_RESOLVED", response.data);
        localStorage.setItem(
          "settings_resolved",
          JSON.stringify(response.data)
        );

        return response.data;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to load resolved settings"
        );
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async loadScopedSettings({ commit }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        const response = await api.get(`${SETTINGS_API_URL}/scoped/`, {
          errorNotify: true,
        });

        commit("SET_SCOPED", response.data);
        return response.data;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to load scoped settings"
        );
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateSetting(
      { dispatch, commit },
      { scope, scope_id, namespace, key, value, refresh = true }
    ) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        await api.put(
          `${SETTINGS_API_URL}/${scope}/${scope_id}/${namespace}/${key}/`,
          { value }
        );

        if (refresh) {
          await dispatch("loadScopedSettings");
          await dispatch("loadResolvedSettings", { force: true });
        }
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to update setting"
        );
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async resetSetting(
      { dispatch, commit },
      { scope, scope_id, namespace, key }
    ) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        await api.delete(
          `${SETTINGS_API_URL}/${scope}/${scope_id}/${namespace}/${key}/reset/`
        );

        await dispatch("loadScopedSettings");
        await dispatch("loadResolvedSettings", { force: true });
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to reset setting"
        );
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
  },

  getters: {
    settings: (state) => state.resolved,
    scopedSettings: (state) => state.scoped,
    settingsLoading: (state) => state.loading,
    settingsError: (state) => state.error,
  },
};
